import { zodResolver } from '@hookform/resolvers/zod';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Button,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import Input from '@mui/joy/Input';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Switch from '@mui/joy/Switch';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useBranches from '../../hooks/usebranches';
import useCategories from '../../hooks/useCategories';
import { useLibraryFiles } from '../../hooks/useLibraryFiles';
import useSubjects from '../../hooks/useSubjects';
import { addFile } from '../../redux/services/libraryServices';
import { FileUploadButton } from './fileUploadButton';

const uploadSchema = z.object({
  title: z.string().min(1, 'Naam is verplicht'),
  category: z.string().min(1, 'Categorie is verplicht'),
  subject: z.string().min(1, 'Onderwerp is verplicht'),
  branch: z.string().min(1, 'Vestiging is verplicht'),
  file: z.instanceof(File).refine((file) => file.size > 0, {
    message: 'Bestand is verplicht',
  }),
  privacy: z.boolean(),
});

type UploadFormData = z.infer<typeof uploadSchema>;

export const UploadForm = (props: {
  setOpen: (_: boolean) => void;
  show: boolean;
}) => {
  const { setOpen, show } = props;
  const [loading, setLoading] = useState(false);
  const branchesQuery = useBranches();
  const categoryQuery = useCategories();
  const subjectQuery = useSubjects();
  const { t } = useTranslation();
  const libraryFilesQuery = useLibraryFiles();

  const defaultFile = new File([], '');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      title: '',
      category: '',
      subject: '',
      branch: '',
      file: defaultFile,
      privacy: false,
    },
  });

  const handleFormSubmit = async (data: UploadFormData) => {
    try {
      setLoading(true);
      await addFile(
        data.title,
        data.category,
        data.subject,
        data.file,
        data.privacy,
        data.branch
      );
      await libraryFilesQuery.refetch();
      setOpen(false);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (
    !categoryQuery.isSuccess ||
    !subjectQuery.isSuccess ||
    !branchesQuery.isSuccess
  ) {
    return null;
  }

  const branches = branchesQuery.data || [];
  const subjects = subjectQuery.data || [];
  const categories = categoryQuery.data || [];

  return (
    <Modal open={show} onClose={() => setOpen(false)}>
      <ModalDialog sx={{ minWidth: '30vw' }}>
        <DialogTitle>{t('library.upload_file')}</DialogTitle>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex flex-col space-y-5"
        >
          {/* Name */}
          <FormControl error={!!errors.title}>
            <FormLabel>Naam</FormLabel>
            <Input
              placeholder={t('library.table_header_name')}
              {...register('title')}
            />
            {errors.title && (
              <Typography color="danger">{errors.title.message}</Typography>
            )}
          </FormControl>

          {/* Upload file */}
          <FormControl error={!!errors.file}>
            <FormLabel>Bestand</FormLabel>
            <FileUploadButton onSelect={(file) => setValue('file', file)} />
            {!!watch('file') && (
              <Typography>{(watch('file') as File).name}</Typography>
            )}
            {errors.file && (
              <Typography color="danger">{errors.file.message}</Typography>
            )}
          </FormControl>

          {/* Category dropdown */}
          <FormControl error={!!errors.category}>
            <FormLabel>Categorie</FormLabel>
            <Select
              value={watch('category')}
              onChange={(e, newValue) => {
                if (typeof newValue === 'string') {
                  setValue('category', newValue);
                  return;
                }
                setValue('category', '');
              }}
              placeholder="Categorie"
            >
              {categories.map((category: any) => (
                <Option key={category.label} value={category.label as string}>
                  {category.label}
                </Option>
              ))}
            </Select>
            {errors.category && (
              <Typography color="danger">{errors.category.message}</Typography>
            )}
          </FormControl>

          {/* Subjects */}
          <FormControl error={!!errors.subject}>
            <FormLabel>Onderwerp</FormLabel>
            <Select
              value={watch('subject')}
              onChange={(e, newValue) => {
                if (typeof newValue === 'string') {
                  setValue('subject', newValue);
                  return;
                }
                setValue('subject', '');
              }}
              placeholder="Onderwerp"
            >
              {subjects.map((subject: any) => (
                <Option key={subject.label} value={subject.label}>
                  {subject.label}
                </Option>
              ))}
            </Select>
            {errors.subject && (
              <Typography color="danger">{errors.subject.message}</Typography>
            )}
          </FormControl>

          {/* Branch select */}
          <FormControl error={!!errors.branch}>
            <FormLabel>Vestiging</FormLabel>
            <Select
              placeholder="Vestiging"
              value={watch('branch')}
              onChange={(e, newValue) => {
                if (typeof newValue === 'string') {
                  setValue('branch', newValue);
                  return;
                }
                setValue('branch', '');
              }}
            >
              {branches.map((branch) => (
                <Option key={branch.name} value={branch.name}>
                  {branch.name}
                </Option>
              ))}
            </Select>
            {errors.branch && (
              <Typography color="danger">{errors.branch.message}</Typography>
            )}
          </FormControl>

          {/* Public private toggle */}
          <FormControl>
            <Stack direction="row" spacing={2} alignItems={'center'}>
              <FormLabel>Privé?</FormLabel>
              <Switch
                sx={{ ml: 1 }}
                checked={watch('privacy')}
                onChange={(e) => {
                  setValue('privacy', e.target.checked);
                }}
              />
            </Stack>
          </FormControl>

          <Stack direction={'row'} spacing={1}>
            <Button
              startDecorator={<CloseRoundedIcon />}
              onClick={() => {
                setOpen(false);
              }}
              color="neutral"
              variant="outlined"
            >
              Annuleren
            </Button>
            <Button
              startDecorator={<CheckBoxRoundedIcon />}
              type="submit"
              disabled={loading}
            >
              Aanmaken
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};
